import { textToHtml } from '../utils/emailFormatting.js';
import { shortenArtistName, sanitizeTrackTitle } from './sharedSanitizers.js';

const DEFAULT_CC = 'goldfarb@freshlybakedstudios.com';

export const generateFollowUp = (artistData, followUpNumber) => {
  const { tracks_data, outreach_report } = artistData;
  const artistName = shortenArtistName(artistData.name);
  const greeting = artistData.greeting || `Hey ${artistName},`;
  const isManager = artistData.recipient_type === 'manager';

  // Track title for the followup. target_track_name is the ONLY trusted
  // source — it's the track pitched in the initial outreach and what the
  // recipient's subject line already says.
  //
  // The previous fallback to outreach_report.primary_track_title was the
  // source of the Paola Decanini drift bug (May 13 2026 followup cited
  // "Un Sueño en el Mar" while May 8 initial cited "Mi Tesoro"). That
  // production_outreach_reports row is generated independently by Step 2b
  // of outreach-prep and routinely picks a different track than what ends
  // up in regression_prospects.target_track_name. Across the V6 send
  // history those tables disagreed for 14% of prospects.
  //
  // Strict rule now: target_track_name OR a tracks_data lookup whose ISRC
  // matches target_track_isrc — never outreach_report. If neither is
  // resolvable, fall back to a generic "your track" so the email still
  // sends but the subject won't lie about a different release.
  let rawTrackTitle = artistData.target_track_name;
  if (!rawTrackTitle) {
    // Last-resort: tracks table only if the ISRC aligns with what was pitched.
    const targetIsrc = artistData.target_track_isrc;
    if (targetIsrc && tracks_data) {
      if (tracks_data.isrc === targetIsrc) rawTrackTitle = tracks_data.top_track;
      else if (tracks_data.recent_track_isrc === targetIsrc) rawTrackTitle = tracks_data.recent_track;
    }
  }
  if (!rawTrackTitle) {
    rawTrackTitle = 'your track';
    console.warn(`[followUpSequence] WARNING: prospect ${artistData.id} (${artistData.name}) has no target_track_name and no ISRC-aligned fallback. Subject will say "your track" instead of a specific title.`);
  }
  const trackTitle = sanitizeTrackTitle(rawTrackTitle) || rawTrackTitle;

  const followUps = {
    1: {
      // Day 3 — Soft callback to the original track + playlist drop + open
      // ask for anything new. The "get a feel for my sensibility" phrasing
      // and the "I'd love to hear" closer are intentional voice choices —
      // genuine, not pitchy. This is the warm follow-up; the harder sells
      // come in followups 2-5.
      content: isManager
        ? `${greeting}

Meant to follow up on ${artistName}. Here's a playlist of recent production and mix work so you can get a feel for my sensibility:
https://open.spotify.com/playlist/4lRBYgUm364VNBUfx047oL

anything new in the works for ${artistName}? send it over, I'd love to hear it.

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
        : `${greeting}

Meant to follow up on '${trackTitle}'. Here's a playlist of recent production and mix work so you can get a feel for my sensibility:
https://open.spotify.com/playlist/4lRBYgUm364VNBUfx047oL

got anything new in the works? send it over, I'd love to hear it.

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    },
    2: {
      // Day 5 — Analyzer tool as the consistency play.
      // Frames the analyzer as a way for the artist's music to "land more
      // consistently" — peer-cohort scan flags which production levers are
      // dragging conversion. Free, so it's a low-friction value drop.
      content: isManager
        ? `${greeting}

Also built a tool that helps tracks land more consistently, scans against converting peers in ${artistName}'s tier and genre, flags which production levers are dragging conversion. The kind of breakdown engineers charge $150 for. Free.

analyze.freshlybakedstudios.com

here's me walking through a full scan if you want to see it first: https://www.youtube.com/watch?v=HOkvWpZX6JQ

want me to run ${artistName}'s latest through it? takes me two minutes, you get the full report before the next release.

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
        : `${greeting}

Also built a tool that helps tracks land more consistently, scans against converting peers in your tier and genre, flags which production levers are dragging conversion. The kind of breakdown engineers charge $150 for. Free.

analyze.freshlybakedstudios.com

here's me walking through a full scan if you want to see it first: https://www.youtube.com/watch?v=HOkvWpZX6JQ

want me to run '${trackTitle}' through it? takes me two minutes, you get the full report.

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    },
    3: {
      // Day 7 — Concrete demo offer
      content: `${greeting}

Send me a rough demo or voice memo. I'll build out a production preview. $500, back in your inbox in three days. Easier than going back and forth, you just hear it.

which song would you want built out?

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    },
    4: {
      // Day 10 — Shared risk + infrastructure proof points.
      // The "we both win" line is hollow without the receipts behind it.
      // Concord publishing + 300/Elektra distro + Sonic Analyser are the
      // actual leverage that makes shared risk a real bet, not just a
      // discount. Without these specifics it reads as a generic ask.
      content: `${greeting}

Also do shared risk deals. Lower upfront, percentage on the backend.

My publishing catalog is with Concord, distro with 300 and Elektra. With the Sonic Analyser's playlist output and virality predictor, we've got a much better chance of moving the needle than most.

worth 20 minutes? cal.freshlybakedstudios.com/freshlybakedstudios/30min

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    },
    5: {
      // Day 14 — Clean close
      content: `${greeting}

Last one from me. when the next record starts moving, who's mixing it?

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    },
    6: {
      // Day ~30 (22 business days) — the "kept thinking about it" nudge.
      // Hormozi delta #12 (owner-approved 2026-08-23): sequence used to end at
      // day 14; this copies the shape of the April Sam Sun follow-up — our
      // single best-performing email ever. Short, genuine, no ask beyond a yes.
      content: isManager
        ? `${greeting}

kept thinking about ${artistName}'s catalog. couple production moves i'd try on the next record, still hearing them in my head. want the list?

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
        : `${greeting}

kept thinking about '${trackTitle}'. couple production moves i'd try on the next one, still hearing them in my head. want the list?

Alexander
instagram.com/alexanderalmgren
freshlybakedstudios.com`
    }
  };

  const followUp = followUps[followUpNumber];

  if (!followUp) {
    return null; // No more followups
  }

  return {
    subject: `Re: ${artistData.name} - ${trackTitle} moved me`,
    from: "almgren@freshlybakedstudios.com",
    to: artistData.email,
    cc: DEFAULT_CC,
    content: followUp.content,
    htmlContent: textToHtml(followUp.content)
  };
};

// Export followup schedule (business days after initial email — weekdays only)
export const FOLLOWUP_SCHEDULE = {
  1: 3,   // Biz day 3  — Production/mix offer + portfolio
  2: 5,   // Biz day 5  — Analyzer tool (consistency play)
  3: 7,   // Biz day 7  — Demo offer ($500)
  4: 10,  // Biz day 10 — Shared risk
  5: 14   // Biz day 14 — Clean close
};
